import { OmsProduct, OmsReport } from "../lusca-search-api/generated-src";

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function strings(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

function timestamp(value: unknown): boolean {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}

function count(value: unknown): boolean {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

export function isStoredOmsReport(
  value: unknown,
  product: OmsProduct
): value is OmsReport {
  return (
    record(value) &&
    value.product === product &&
    typeof value.snapshotId === "string" &&
    strings(value.columns) &&
    timestamp(value.sourceRefreshedAt) &&
    timestamp(value.exportedAt) &&
    count(value.rowCount) &&
    count(value.totalBytes) &&
    (value.columnTypes === undefined ||
      (record(value.columnTypes) &&
        Object.values(value.columnTypes).every(
          (type) =>
            typeof type === "string" &&
            ["string", "number", "date", "datetime"].includes(type)
        ))) &&
    (value.columnValues === undefined ||
      (record(value.columnValues) &&
        Object.values(value.columnValues).every(
          (item) =>
            record(item) &&
            strings(item.values) &&
            typeof item.complete === "boolean"
        ))) &&
    Array.isArray(value.parts) &&
    value.parts.every(
      (item) =>
        record(item) &&
        typeof item.id === "string" &&
        typeof item.fileName === "string" &&
        count(item.rowCount) &&
        count(item.sizeBytes)
    )
  );
}

function sourceIdentity(report: OmsReport) {
  return JSON.stringify([
    report.product,
    report.snapshotId,
    report.sourceRefreshedAt,
    report.exportedAt,
    report.rowCount,
    report.totalBytes,
    report.columns,
    report.parts,
  ]);
}

export default function refreshOmsReportMetadata(
  pinned: OmsReport,
  available: OmsReport
): OmsReport {
  // Suggestions may improve without changing the source accepted by an export.
  if (sourceIdentity(pinned) !== sourceIdentity(available)) return pinned;
  const columnTypes = { ...pinned.columnTypes, ...available.columnTypes };
  const columnValues = { ...pinned.columnValues };
  Object.entries(available.columnValues || {}).forEach(([name, choices]) => {
    const previous = columnValues[name];
    if (
      !previous ||
      choices.complete ||
      (!previous.complete && choices.values.length >= previous.values.length)
    )
      columnValues[name] = choices;
  });
  if (
    JSON.stringify(columnTypes) === JSON.stringify(pinned.columnTypes || {}) &&
    JSON.stringify(columnValues) === JSON.stringify(pinned.columnValues || {})
  )
    return pinned;
  return { ...pinned, columnTypes, columnValues };
}
